import React from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Instagram,
  Facebook,
  Email,
  ArrowForward,
  Language,
} from "@mui/icons-material";
import { SOCIALS } from "../constants/socials";
import { useNavigate } from "react-router-dom";

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const footerLinks = {
    column1: [
      { label: "Home", href: "/", type: "route" },
      { label: "Stays", href: "/properties", type: "route" },
    ],
    column2: [
      { label: "Experiences", href: "/experiences", type: "route" },
      { label: "About", href: "/about", type: "route" },
    ],
  };

  const handleNavClick = (link) => {
    if (link.type === "route") {
      navigate(link.href);
    } else {
      // For anchor links, scroll to the section (only works on landing page)
      const element = document.querySelector(link.href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const socialLinks = [
    { icon: Language, href: SOCIALS.google, label: "Google Business" },
    { icon: Instagram, href: SOCIALS.instagram, label: "Instagram" },
    { icon: Facebook, href: SOCIALS.facebook, label: "Facebook" },
    { icon: Email, href: `mailto:${SOCIALS.email}`, label: "Email" },
  ];

  return (
    <Box
      id="contact"
      sx={{
        backgroundColor: "#000000",
        color: "white",
        pt: { xs: 6, sm: 8, md: 10 },
        pb: { xs: 4, sm: 5, md: 6 },
        minHeight: 0,
        position: 'relative',
        zIndex: 10,
        overflow: 'hidden',
        boxShadow: '0 0 0 100vmax #000',
        clipPath: 'inset(0 -100vmax)',
        mx: 2
      }}
    >
      <Container maxWidth="lg" sx={{ backgroundColor: 'transparent', position: 'relative', zIndex: 1, p: 0 }}>
        {/* Main Footer Content */}
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "start", md: "center" },
            minHeight: "300px",
            flexDirection: { xs: "column", md: "row" },
            backgroundColor: "#000", // Prevent white showing during fade-up
          }}
        >
          {/* Left Section - CTA */}
          <Box sx={{ flex: 1, mb: { xs: 8, md: 0 }, pr: { md: 4 } }}>
            <img
              src="/icon-white.svg"
              alt="Aspire Ventures Logo"
              style={{ width: "72px", marginBottom: "40px" }}
            />
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontFamily: "Monument-Private",
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                fontWeight: "bold",
                lineHeight: 1.1,
                mb: 3,
                color: "white",
              }}
            >
              Ready for an amazing vacation?
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: "1.1rem",
                lineHeight: 1.6,
                mb: 4,
                color: "rgba(255, 255, 255, 0.8)",
                maxWidth: "400px",
              }}
            >
              Book your stay with Aspire Ventures today!
            </Typography>

            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              sx={{
                backgroundColor: "white",
                color: "#000000",
                textTransform: "none",
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 6,
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 25px rgba(255, 255, 255, 0.2)",
                },
              }}
              onClick={() => navigate("/contact")}
            >
              Contact us
            </Button>
          </Box>

          {/* Vertical Divider */}
          <Box
            sx={{
              width: "1px",
              height: "250px",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              mx: { xs: 2, md: 6 },
              display: { xs: "none", md: "block" },
            }}
          />

          {/* Right Section - Links */}
          <Box sx={{ flex: 1 }}>
            <Grid container spacing={4}>
              {/* Navigation Links */}
              <Grid item size={{ xs: 6 }}>
                <Box sx={{ backgroundColor: "#000" }}>
                  {footerLinks.column1.map((link, index) => (
                    <Typography
                      key={index}
                      onClick={() => handleNavClick(link)}
                      sx={{
                        display: "block",
                        color: "rgba(255, 255, 255, 0.7)",
                        textDecoration: "none",
                        fontSize: { xs: "1.5rem", sm: "2rem" },
                        fontWeight: 400,
                        mb: 2,
                        transition: "all 0.3s ease",
                        fontFamily: "Monument-Private",
                        cursor: "pointer",

                        "&:hover": {
                          color: "white",
                          transform: "translateX(5px)",
                        },
                      }}
                    >
                      {link.label}
                    </Typography>
                  ))}
                </Box>
              </Grid>

              <Grid item size={{ xs: 6 }}>
                <Box sx={{ backgroundColor: "#000" }}>
                  {footerLinks.column2.map((link, index) => (
                    <Typography
                      key={index}
                      onClick={() => handleNavClick(link)}
                      sx={{
                        display: "block",
                        color: "rgba(255, 255, 255, 0.7)",
                        textDecoration: "none",
                        fontSize: { xs: "1.5rem", sm: "2rem" },
                        fontFamily: "Monument-Private",
                        fontWeight: 400,
                        mb: 2,
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                        "&:hover": {
                          color: "white",
                          transform: "translateX(5px)",
                        },
                      }}
                    >
                      {link.label}
                    </Typography>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Divider */}
        <Box
          sx={{
            width: "100%",
            height: "1px",
            my: { xs: 4, sm: 5, md: 6 },
          }}
        />

        {/* Bottom Section */}
        <Grid container spacing={4} alignItems="center" sx={{ backgroundColor: '#000', position: 'relative', zIndex: 2 }}>
          {/* Copyright */}
          <Grid item size={{ xs: 12, md: 6 }}>
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255, 255, 255, 0.5)",
                fontSize: "0.9rem",
                textAlign: { xs: "left", md: "left" },
              }}
            >
              © 2025 Aspire Ventures Goa
            </Typography>
          </Grid>

          {/* Social Media Icons */}
          <Grid item size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: { xs: "flex-start", md: "flex-end" },
                backgroundColor: "#000",
              }}
            >
              {socialLinks.map((social, index) => (
                <IconButton
                  key={index}
                  href={social.href}
                  aria-label={social.label}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    width: 56,
                    height: 56,
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    borderRadius: "50%",
                    color: "rgba(255, 255, 255, 0.7)",
                    transition: "all 0.3s cubic-bezier(.4,2,.6,1)",
                    fontSize: 28,
                    backgroundColor: "transparent",
                    '&:hover': {
                      backgroundColor: '#fff',
                      color: '#000',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.10)',
                      borderColor: '#fff',
                    },
                  }}
                >
                  <social.icon fontSize="inherit" />
                </IconButton>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Footer;
